<!-- Таб "Информация" для ЭЗЗ с единым новым представлением титулов. -->
<template>
  <div class="transport-order-preview__content">
    <FlowDetails
      v-if="documentFlowVisible"
      :flow="documentFlow"
      :title-details="flowTitleDetails"
      :read-only="readOnly"
      :inspectable="readOnly"
      :variant="variant"
      @title-action="handleTitleAction"
    />

  </div>
</template>

<script>
import { formatDate, formatDateAndTime } from "@/utils/dateUtils";
import { edoVehicleOwnershipTypeLabel } from "@/utils/edoVehicleUtils";
import { formatBigNumber } from "@/utils/numberUtils";
import FlowDetails from "@/components/Edo/TransportOrder/FlowDetails.vue";

export default {
  name: "EdoTransportOrderPreviewTab",

  components: {
    FlowDetails,
  },

  props: {
    document: {
      type: Object,
      required: true,
    },
    sabyUrl: {
      type: String,
      default: null,
    },
    readOnly: {
      type: Boolean,
      default: false,
    },
    variant: {
      type: String,
      default: "card",
      validator: (value) => ["card", "embedded", "flat"].includes(value),
    },
  },

  emits: ["edit", "edit-title2", "create-consignment-note"],

  computed: {
    documentData() {
      return this.document.parsed_data || {};
    },

    // Передаёт в общий flow только реальные данные двух титулов ЭЗЗ. Статусы
    // и действия берутся из backend document_flow внутри общего компонента.
    flowTitleDetails() {
      return [
        {
          key: "title1",
          label: "Титул 1",
          role: "Грузоотправитель",
          title: "Данные грузоотправителя",
          description: "Исходные условия перевозки, участники, маршрут и груз.",
          sections: this.title1Sections,
        },
        {
          key: "title2",
          label: "Титул 2",
          role: "Перевозчик",
          title: "Данные перевозчика",
          description: "Транспорт, водитель и сведения перевозчика для принятия груза.",
          sections: this.title2Sections,
        },
      ].map((title) => ({
        ...title,
        hasData: title.sections.some((section) => section.rows?.length),
      }));
    },

    // Секции титула 1: исходные условия заявки, которые заполняет грузоотправитель.
    title1Sections() {
      return [
        {
          key: "document",
          title: "Документ",
          rows: [
            this.previewRow(
              "document_number",
              "Номер",
              this.documentData.document_number || this.document.document_number
            ),
            this.previewRow(
              "document_date",
              "Дата",
              this.formatDateValue(
                this.documentData.document_date || this.document.document_date
              )
            ),
            this.previewRow("price_amount", "Стоимость", this.moneyValue(this.documentData.price_amount)),
            this.previewRow("payment_terms", "Условия оплаты", this.documentData.payment_terms),
            this.previewRow("comment", "Комментарий", this.documentData.comment),
          ],
        },
        {
          key: "participants",
          title: "Участники",
          rows: [
            this.participantRow("customer", "Заказчик"),
            this.participantRow("shipper", "Грузоотправитель"),
            this.participantRow("consignee", "Грузополучатель"),
            this.participantRow("carrier", "Перевозчик"),
          ],
        },
        {
          key: "route",
          title: "Маршрут",
          rows: [
            this.pointRow("loading_point", "Погрузка"),
            this.pointRow("delivery_point", "Выгрузка"),
          ],
        },
        {
          key: "cargo",
          title: "Груз",
          rows: this.cargoRows,
        },
        {
          key: "conditions",
          title: "Дополнительные условия",
          rows: [
            this.previewRow("temperature_condition", "Температурный режим", this.documentData.temperature_condition),
            this.previewRow("humidity_condition", "Влажность", this.documentData.humidity_condition),
          ],
        },
      ].map((section) => ({
        ...section,
        rows: section.rows.filter(Boolean),
      }));
    },

    // Секции титула 2: фактический транспорт и водитель, которые заполняет перевозчик.
    title2Sections() {
      const sections = [
        {
          key: "transport",
          title: "Транспорт",
          rows: [
            this.vehicleRow("vehicle", "ТС", this.documentData.vehicle),
            this.vehicleRow("trailer", "Прицеп", this.firstTrailer),
          ],
        },
        {
          key: "driver",
          title: "Водитель",
          rows: [
            this.driverRow,
          ],
        },
      ].map((section) => ({
        ...section,
        rows: section.rows.filter(Boolean),
      }));

      const errorSection = this.titleErrorSection(2);
      return errorSection ? [...sections, errorSection] : sections;
    },

    // Flow с backend — источник правды по этапам и доступным действиям ЭЗЗ.
    // Фронт только отображает эти решения, чтобы не размазывать бизнес-условия.
    documentFlow() {
      return this.document.document_flow || {};
    },

    documentFlowVisible() {
      return Array.isArray(this.documentFlow.steps) && this.documentFlow.steps.length > 0;
    },

    // Превращает массив грузов в одну строку предпросмотра.
    cargoRows() {
      const items = Array.isArray(this.documentData.cargo_items)
        ? this.documentData.cargo_items
        : [];
      if (!items.length) return [this.previewRow("cargo_empty", "Груз", null)];

      return items.flatMap((item, index) => {
        const parts = [
          item.title,
          item.quantity_places ? `${item.quantity_places} мест` : null,
          item.weight_gross_kg ? `${item.weight_gross_kg} кг` : null,
          item.volume_m3 ? `${item.volume_m3} м³` : null,
        ].filter(Boolean);

        return [
          this.previewRow(
            `cargo_${index}`,
            `Груз ${index + 1}`,
            parts.join(", ")
          ),
          this.previewRow(
            `cargo_${index}_description`,
            `Дополнительные сведения груза ${index + 1}`,
            item.description
          ),
        ].filter(Boolean);
      });
    },

    // Возвращает первый прицеп, потому что текущий UI показывает один основной прицеп.
    firstTrailer() {
      return Array.isArray(this.documentData.trailers)
        ? this.documentData.trailers[0]
        : null;
    },

    // Собирает строку водителя из parsed_data титула 2.
    driverRow() {
      const drivers = this.documentData.drivers;
      const driver = Array.isArray(drivers) ? drivers[0] : this.documentData.driver;
      if (!driver) return this.previewRow("driver", "Водитель", null);

      const name = [
        driver.last_name || driver.lastName,
        driver.first_name || driver.firstName,
        driver.middle_name || driver.middleName || driver.patronymic,
      ].filter(Boolean).join(" ");
      const details = [
        name,
        driver.phone,
        this.driverLicenseValue(driver),
      ].filter(Boolean);

      return this.previewRow("driver", "Водитель", details.join(", "));
    },
  },

  methods: {
    handleTitleAction({ action }) {
      if (action.type === "saby" && this.sabyUrl) {
        window.open(this.sabyUrl, "_blank");
      } else if (["edit", "edit-title1"].includes(action.type)) {
        this.$emit("edit");
      } else if (action.type === "edit-title2") {
        this.$emit("edit-title2");
      } else if (action.type === "create-consignment-note") {
        this.$emit("create-consignment-note");
      }
    },

    transportOrderTitle(number) {
      const titles = Array.isArray(this.document?.transport_order_titles)
        ? this.document.transport_order_titles
        : [];

      return titles.find((title) => title.number === number) || null;
    },

    // Ошибки — реальные данные конкретного титула, а не вычисление статуса.
    // Поэтому сохраняем их в новой панели, пока цвет и действия приходят с backend.
    titleErrorSection(number) {
      const title = this.transportOrderTitle(number);
      const messages = [
        title?.error_message,
        ...(Array.isArray(title?.error_messages) ? title.error_messages : []),
      ].filter(Boolean);
      const uniqueMessages = [...new Set(messages)];
      if (!uniqueMessages.length) return null;

      return {
        key: `title${number}_errors`,
        title: "Ошибки титула",
        icon: "document",
        rows: uniqueMessages.map((message, index) =>
          this.previewRow(`title${number}_error_${index}`, "Ошибка", message)
        ),
      };
    },

    // Создаёт строку read-only предпросмотра с fallback на "—".
    previewRow(key, label, value) {
      return {
        key,
        label,
        value: this.formatPreviewValue(value),
      };
    },

    // Собирает данные участника: название, ИНН/КПП и адрес.
    participantRow(key, label) {
      const participant = this.documentData[key];
      if (!participant) return this.previewRow(key, label, null);

      const details = [
        participant.name,
        participant.inn ? `ИНН ${participant.inn}` : null,
        participant.kpp ? `КПП ${participant.kpp}` : null,
        this.addressValue(participant.address || participant),
      ].filter(Boolean);

      return this.previewRow(key, label, details.join(", "));
    },

    // Собирает строку точки маршрута с адресом и плановой датой.
    pointRow(key, label) {
      const point = this.documentData[key];
      if (!point) return this.previewRow(key, label, null);

      const details = [
        this.addressValue(point),
        point.planned_at ? this.formatDateTimeValue(point.planned_at) : null,
      ].filter(Boolean);

      return this.previewRow(key, label, details.join(", "));
    },

    // Собирает строку транспорта или прицепа для предпросмотра титула 2.
    vehicleRow(key, label, vehicle) {
      if (!vehicle) return this.previewRow(key, label, null);

      const details = [
        vehicle.registration_number || vehicle.registrationNumber,
        vehicle.model,
        vehicle.etrn_vehicle_type || vehicle.etrnVehicleType,
        edoVehicleOwnershipTypeLabel(vehicle.ownership_type || vehicle.ownershipType),
        vehicle.body_type || vehicle.bodyType,
        this.ownershipDocumentValue(vehicle.ownership_document || vehicle.ownershipDocument),
      ].filter(Boolean);

      return this.previewRow(key, label, details.join(", "));
    },

    driverLicenseValue(driver) {
      const number = driver.license_number || driver.licenseNumber;
      if (!number) return "";

      return `ВУ ${driver.license_series || driver.licenseSeries || ""} ${number}`.trim();
    },

    ownershipDocumentValue(document) {
      if (!document) return "";

      return [
        document.name || document.document_name,
        document.number || document.document_number,
        this.formatDateValue(document.date || document.document_date),
      ].filter((value) => value && value !== "—").join(" ");
    },

    // Нормализует адрес из разных форматов parsed_data.
    addressValue(value) {
      if (!value) return "";
      if (typeof value === "string") return value;

      return (
        value.address ||
        value.address_text ||
        value.full_address ||
        [value.city, value.street, value.house].filter(Boolean).join(", ")
      );
    },

    // Показывает длинное тире вместо пустых значений.
    formatPreviewValue(value) {
      if (value === null || value === undefined || value === "") return "—";
      return value;
    },

    moneyValue(value) {
      if (value === null || value === undefined || value === "") return null;
      return `${formatBigNumber(value)} ₽`;
    },

    // Форматирует дату документа в человекочитаемый вид.
    formatDateValue(value) {
      return value ? formatDate(value) : "—";
    },

    // Форматирует дату-время маршрута в человекочитаемый вид.
    formatDateTimeValue(value) {
      return value ? formatDateAndTime(value) : "—";
    },
  },
};
</script>

<style scoped>
.transport-order-preview__content {
  display: flex;
  flex-direction: column;
  gap: 16px;
}
</style>
